import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  VOID_REASONS,
  composeVoidReason,
  journalSourceLabel,
  type Contact,
  type VoidReasonCode,
} from '@accounting/shared';
import {
  useJournalEntry,
  useVoidJournalEntry,
} from '../../hooks/useJournalEntries';
import { useAccounts } from '../../hooks/useAccounts';
import { useContacts } from '../../hooks/useContacts';
import { useClasses, useLocations } from '../../hooks/useDimensions';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AttachmentsPanel } from '../../components/attachments/AttachmentsPanel';
import { BackLink } from '../../components/shell/BackLink';
import { ReclassifyModal } from '../../components/journal/ReclassifyModal';
import { useReconciliation } from '../../hooks/useReconciliation';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/journal/$id')({
  component: JournalEntryDetailPage,
});

/** Links a journal-line contact to the right place: customers → Customer Center,
 *  vendors → Vendor Center, employees → Employees, anything else → Vendors list. */
function ContactTag({ contact }: { contact: Contact }) {
  const label = `Contact: ${contact.name}`;
  const cls = 'tag';
  const style = { textDecoration: 'none' } as const;
  if (contact.contactType === 'customer') {
    return (
      <Link to="/customer-center/$id" params={{ id: contact.id }} className={cls} style={style} title="Customer">
        {label}
      </Link>
    );
  }
  if (contact.contactType === 'vendor') {
    return (
      <Link to="/vendor-center/$id" params={{ id: contact.id }} className={cls} style={style} title="Vendor">
        {label}
      </Link>
    );
  }
  if (contact.contactType === 'employee') {
    return (
      <Link to="/employees" className={cls} style={style} title="Employee">
        {label}
      </Link>
    );
  }
  return (
    <Link to="/vendors" className={cls} style={style} title="Contact">
      {label}
    </Link>
  );
}

function JournalEntryDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();

  const { data: entry, isLoading, error } = useJournalEntry(companyId, id);
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: contacts = [] } = useContacts(companyId, { includeInactive: true });
  const { data: locations = [] } = useLocations(companyId, { includeInactive: true });
  const { data: classes = [] } = useClasses(companyId, { includeInactive: true });
  const voidMutation = useVoidJournalEntry(companyId);

  const [showVoidForm, setShowVoidForm] = useState(false);
  // A picked reason plus an optional note; the stored string is composed from
  // both, so the audit log stays greppable by label.
  const [voidReasonCode, setVoidReasonCode] = useState<VoidReasonCode>('duplicate');
  const [voidNote, setVoidNote] = useState('');
  const [voidOverride, setVoidOverride] = useState(false);
  const [showReclassify, setShowReclassify] = useState(false);

  // The GL account frozen against reclassification: the one the locking
  // reconciliation actually reconciled. Only fetched when the entry is locked.
  const { data: lockingRecon, isLoading: lockingReconLoading } = useReconciliation(
    entry?.isLocked ? companyId : null,
    entry?.lockedByReconciliationId ?? '',
  );

  const accountById = new Map(accounts.map((a) => [a.id, a]));
  // The account whose line the locking reconciliation actually froze. Naming it
  // is the difference between "this transaction is locked" (wrong — the other
  // legs are still reconcilable) and "the 2002 leg is reconciled" (true).
  const lockedAccount =
    entry?.isLocked && lockingRecon?.glAccountId
      ? accountById.get(lockingRecon.glAccountId) ?? null
      : null;
  const contactById = new Map(contacts.map((c) => [c.id, c]));
  const locationById = new Map(locations.map((l) => [l.id, l]));
  const classById = new Map(classes.map((c) => [c.id, c]));

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !entry) {
    return (
      <div>
        <p className="field-error">Failed to load entry.</p>
        <BackLink to="/journal">Back</BackLink>
      </div>
    );
  }

  const totals = entry.lines.reduce(
    (acc, l) => ({
      debits: acc.debits + parseFloat(l.debit),
      credits: acc.credits + parseFloat(l.credit),
    }),
    { debits: 0, credits: 0 },
  );

  const voidError =
    voidMutation.error instanceof ApiError
      ? (voidMutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : voidMutation.error?.message;

  return (
    <div>
      <BackLink to="/journal" style={{ fontSize: 14 }}>
        Back
      </BackLink>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginTop: 8,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>
            {entry.entryNumber}
            {entry.isVoid && (
              <span className="tag bad" style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                void
              </span>
            )}
            {entry.isLocked && !entry.isVoid && (
              <span
                className="tag warn"
                style={{ marginLeft: 12, verticalAlign: 'middle' }}
                title={
                  lockedAccount
                    ? `Only the ${lockedAccount.code} ${lockedAccount.name} line is frozen by a completed reconciliation. Every other line on this entry can still be reconciled against its own statement.`
                    : 'One line on this entry is frozen by a completed reconciliation. The other lines can still be reconciled against their own statements.'
                }
              >
                {lockedAccount ? `reconciled · ${lockedAccount.code}` : 'reconciled'}
              </span>
            )}
          </h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {formatAccountingDate(entry.date)} · source: {journalSourceLabel(entry.source)}
            {entry.reference && ` · ref: ${entry.reference}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Reclassify is offered for every non-void entry, regardless of
            * source: re-accounting the offset never contradicts the source
            * document (a bill is still that bill), and for a reconciled entry
            * it is the only correct way to fix a wrong expense account. */}
          {!entry.isVoid && (
            <button
              className="btn-secondary"
              onClick={() => setShowReclassify(true)}
              // Don't open until we know which line is frozen — otherwise the
              // reconciled bank row would briefly render as editable and the
              // server would reject the save with a confusing error.
              disabled={lockingReconLoading}
              title={
                entry.isLocked
                  ? 'Reconciled entries can still be re-accounted — the bank side stays frozen, so the reconciliation survives.'
                  : 'Change which accounts this entry posts to.'
              }
            >
              Reclassify
            </button>
          )}
          {(entry.source === 'manual' || entry.source === 'import') && !entry.isVoid && (
            <Link
              to="/journal/$id/edit"
              params={{ id: entry.id }}
              className="btn-secondary"
              style={{ textDecoration: 'none' }}
              title={
                entry.isLocked
                  ? 'This entry is locked by a completed reconciliation. Override lets you edit anyway (owner discretion).'
                  : undefined
              }
            >
              {entry.isLocked ? 'Edit (override lock)' : 'Edit'}
            </Link>
          )}
          <Link
            to="/journal/new"
            search={{ copyFrom: entry.id }}
            className="btn-secondary"
            style={{ textDecoration: 'none' }}
          >
            Copy
          </Link>
          {!entry.isVoid && !showVoidForm && (
            <button
              className="btn-danger"
              onClick={() => {
                setVoidOverride(false);
                setShowVoidForm(true);
              }}
            >
              {entry.isLocked ? 'Void (override lock)' : 'Void entry'}
            </button>
          )}
        </div>
      </div>

      {entry.memo && (
        <p style={{ marginTop: 0 }}>
          <strong>Memo:</strong> {entry.memo}
        </p>
      )}

      {entry.isVoid && entry.voidReason && (
        <div
          className="card"
          style={{
            background: 'var(--danger-bg)',
            borderColor: 'var(--danger-border)',
            color: 'var(--danger)',
            marginBottom: 16,
          }}
        >
          <strong>Voided:</strong> {entry.voidReason}
          {entry.voidedAt && (
            <span style={{ marginLeft: 8, opacity: 0.8 }}>
              ({new Date(entry.voidedAt).toLocaleString()})
            </span>
          )}
        </div>
      )}

      {showVoidForm && (
        <form
          className="card"
          style={{ marginBottom: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            voidMutation.mutate(
              {
                entryId: entry.id,
                voidReason: composeVoidReason(
                  VOID_REASONS.find((r) => r.code === voidReasonCode)!.label,
                  voidNote,
                ),
                override: entry.isLocked ? voidOverride : undefined,
              },
              {
                onSuccess: () => {
                  setShowVoidForm(false);
                  void navigate({ to: '/journal' });
                },
              },
            );
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Void this entry</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Voiding marks this entry as void and excludes it from all balances and
            reports. The original lines stay intact for audit. If you need a true
            reversing entry, post a new one manually with debits and credits flipped.
          </p>
          {entry.isLocked && (
            <div
              className="card"
              style={{
                background: 'var(--tag-warn-bg)',
                color: 'var(--tag-warn-fg)',
                borderColor: 'var(--tag-warn-fg)',
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              <strong>This entry is locked</strong> by a completed reconciliation. Voiding it
              will leave that reconciliation referencing a voided entry — the hash chain
              remains valid but the reconciliation math will no longer tie out. You may want
              to void the reconciliation and redo it after this change.
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={voidOverride}
                  onChange={(e) => setVoidOverride(e.target.checked)}
                />
                I understand — override the reconciliation lock.
              </label>
            </div>
          )}
          <label htmlFor="reason" style={{ display: 'block', marginBottom: 4 }}>
            Reason (required)
          </label>
          <select
            id="reason"
            value={voidReasonCode}
            onChange={(e) => setVoidReasonCode(e.target.value as VoidReasonCode)}
            style={{ width: '100%', maxWidth: 600 }}
          >
            {VOID_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
          <textarea
            value={voidNote}
            onChange={(e) => setVoidNote(e.target.value)}
            rows={2}
            placeholder={
              voidReasonCode === 'other'
                ? 'Required — describe why this entry is being voided'
                : 'Optional detail (which entry it duplicates, what the correct figure was…)'
            }
            maxLength={400}
            style={{ width: '100%', maxWidth: 600, marginTop: 8 }}
          />
          {voidError && (
            <div className="field-error" style={{ marginTop: 8 }}>
              {voidError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="submit"
              className="btn-danger"
              disabled={
                voidMutation.isPending ||
                // "Other" carries no meaning on its own, so it needs the note.
                (voidReasonCode === 'other' && !voidNote.trim()) ||
                (entry.isLocked && !voidOverride)
              }
            >
              {voidMutation.isPending ? 'Voiding…' : 'Confirm void'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowVoidForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <table className="table">
        <thead>
          <tr>
            <th>Account</th>
            <th className="right" style={{ width: 140 }}>Debit</th>
            <th className="right" style={{ width: 140 }}>Credit</th>
            <th
              className="right"
              style={{ width: 150 }}
              title="Account balance immediately after this line posted, signed on the account's normal side."
            >
              Balance
            </th>
            <th>Memo &amp; dimensions</th>
          </tr>
        </thead>
        <tbody>
          {entry.lines.map((line) => {
            const acct = accountById.get(line.accountId);
            const contact = line.contactId ? contactById.get(line.contactId) : null;
            const location = line.locationId ? locationById.get(line.locationId) : null;
            const cls = line.classId ? classById.get(line.classId) : null;
            const hasDims = !!(contact || location || cls);
            return (
              <tr key={line.id}>
                <td>
                  {acct ? (
                    <span>
                      <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)' }}>
                        {acct.code}
                      </span>{' '}
                      {acct.name}
                    </span>
                  ) : (
                    <span className="muted">{line.accountId}</span>
                  )}
                  {/* The lock lives on the LINE, not the entry — mark the leg
                    * that a reconciliation actually froze, so it is obvious the
                    * remaining legs are still free to reconcile. */}
                  {line.lockedByReconciliationId && (
                    <span
                      className="tag warn"
                      style={{ marginLeft: 8, fontSize: 11, verticalAlign: 'middle' }}
                      title="This line is frozen by a completed reconciliation. The other lines on this entry are unaffected and can still be reconciled against their own statements."
                    >
                      reconciled
                    </span>
                  )}
                </td>
                <td className="right">
                  {parseFloat(line.debit) > 0 ? formatCurrency(line.debit) : ''}
                </td>
                <td className="right">
                  {parseFloat(line.credit) > 0 ? formatCurrency(line.credit) : ''}
                </td>
                <td className="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {line.balanceAfter != null
                    ? formatCurrency(line.balanceAfter, true)
                    : <span className="muted">—</span>}
                </td>
                <td>
                  {line.memo ? <div>{line.memo}</div> : null}
                  {hasDims && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        marginTop: line.memo ? 6 : 0,
                      }}
                    >
                      {contact && <ContactTag contact={contact} />}
                      {location && (
                        <Link
                          to="/locations"
                          className="tag"
                          style={{ textDecoration: 'none' }}
                          title="Location"
                        >
                          Location: {location.name}
                        </Link>
                      )}
                      {cls && (
                        <Link
                          to="/classes"
                          className="tag"
                          style={{ textDecoration: 'none' }}
                          title="Class"
                        >
                          Class: {cls.name}
                        </Link>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Totals</td>
            <td className="right" style={{ fontWeight: 600 }}>
              {formatCurrency(totals.debits)}
            </td>
            <td className="right" style={{ fontWeight: 600 }}>
              {formatCurrency(totals.credits)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: 16 }}>
        <AttachmentsPanel companyId={companyId} entityType="journal_entry" entityId={entry.id} />
      </div>

      {showReclassify && (
        <ReclassifyModal
          entry={entry}
          accounts={accounts}
          frozenAccountId={entry.isLocked ? lockingRecon?.glAccountId ?? null : null}
          onClose={() => setShowReclassify(false)}
        />
      )}
    </div>
  );
}
